import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle, Github, Linkedin, Loader2, Mail, Send } from 'lucide-react';
import { useState } from 'react';

const Contact = () => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [formStatus, setFormStatus] = useState({ type: '', message: '' });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
            setFormStatus({
                type: 'error',
                message: 'Please fill in all required fields.'
            });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setFormStatus({
                type: 'error',
                message: 'Please enter a valid email address.'
            });
            return;
        }

        setFormStatus({ type: 'loading', message: 'Sending message...' });

        try {
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('email', formData.email);
            submitData.append('message', formData.message);
            submitData.append('_subject', `Portfolio Contact: Message from ${formData.name}`);
            submitData.append('_captcha', 'false');
            submitData.append('_template', 'table');
            submitData.append('_url', window.location.href);

            const response = await fetch('https://formsubmit.co/tarushagarwal2003@gmail.com', {
                method: 'POST',
                body: submitData
            });

            if (response.ok) {
                setFormStatus({
                    type: 'success',
                    message: 'Message sent successfully! Tarush will get back to you soon.'
                });
                setFormData({ name: '', email: '', message: '' });
            } else {
                throw new Error('Failed to send message');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setFormStatus({
                type: 'error',
                message: 'Failed to send message. Please try again or email directly at tarushagarwal2003@gmail.com'
            });
        }

        setTimeout(() => setFormStatus({ type: '', message: '' }), 8000);
    };

    return (
        <section id="contact" className="section">
            <div className="container">
                <div className="grid md:grid-cols-2 gap-16">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-6xl font-bold mb-8">Set Sail <br /><span className="text-primary-purple">Together.</span></h2>
                        <p className="text-xl text-gray-400 mb-12 font-light">
                            Ready to navigate the Grand Line of innovation? Whether you have a treasure map for a new project or just want to exchange ideas, I'm always up for the adventure.
                        </p>

                        <div className="space-y-6">
                            {[
                                { href: "mailto:tarushagarwal2003@gmail.com", icon: Mail, text: "tarushagarwal2003@gmail.com" },
                                { href: "https://www.linkedin.com/in/tarush10000/", icon: Linkedin, text: "linkedin.com/in/tarush10000" },
                                { href: "https://github.com/tarush10000", icon: Github, text: "github.com/tarush10000" }
                            ].map((item, index) => (
                                <a
                                    key={index}
                                    href={item.href}
                                    target={item.href.startsWith('http') ? "_blank" : "_self"}
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-4 text-gray-400 hover:text-white transition-colors group"
                                >
                                    <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center group-hover:border-primary-purple group-hover:bg-primary-purple/10 transition-all">
                                        <item.icon size={20} />
                                    </div>
                                    <span className="text-lg">{item.text}</span>
                                </a>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="glass-card p-8 border border-white/10 bg-white/[0.02]"
                    >
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <input type="hidden" name="_subject" value={`Portfolio Contact: Message from ${formData.name}`} />
                            <input type="hidden" name="_captcha" value="false" />
                            <input type="hidden" name="_template" value="table" />
                            <input type="hidden" name="_next" value={window.location.href + "?submitted=true"} />

                            <div>
                                <label htmlFor="name" className="block text-gray-400 mb-2 text-sm uppercase tracking-wider">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    placeholder="Gol D. Roger"
                                    required
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="input bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary-purple focus:ring-0 focus:bg-transparent"
                                    autoComplete="name"
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-gray-400 mb-2 text-sm uppercase tracking-wider">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    placeholder="pirateking@grandline.sea"
                                    required
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="input bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary-purple focus:ring-0 focus:bg-transparent"
                                    autoComplete="email"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-gray-400 mb-2 text-sm uppercase tracking-wider">
                                    Message
                                </label>
                                <textarea
                                    name="message"
                                    id="message"
                                    rows="4"
                                    placeholder="You want my treasure? You can have it! I left everything I gathered together in one place. Now you just have to find it!"
                                    required
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    className="input bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary-purple focus:ring-0 focus:bg-transparent min-h-[100px] resize-y"
                                />
                            </div>

                            {formStatus.message && (
                                <div className={`p-4 rounded-lg flex items-center gap-3 text-sm ${formStatus.type === 'success'
                                    ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                    }`}>
                                    {formStatus.type === 'success' && <CheckCircle size={18} />}
                                    {formStatus.type === 'error' && <AlertCircle size={18} />}
                                    {formStatus.type === 'loading' && <Loader2 size={18} className="animate-spin" />}
                                    <span>{formStatus.message}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary w-full justify-center mt-4"
                            >
                                Set Sail <Send size={18} className="ml-2" />
                            </button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
